// Imports ------------------------------------------------------------------->
import { Component, handler } from '../Component'
import { element } from '../CustomElement'
import { i18n } from '../../i18n/I18n'
import { icons } from '../../icons/Icons'

import { FitImageElement } from '../flipp-fit-image/FitImageComponent'
import { LazyImage } from '../flipp-lazy-image/LazyImage'
import { ConfigService, Experience } from '../../services/ConfigService'
import { inject } from '../../services/di'
import { MobileBanner } from '../flipp-mobile-banner/MobileBanner'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export interface ItemDialogDetails {
    itemImageUrl?: string
    merchantLogoUrl?: string
    unavailable?: UnavailableDetails
}

export interface UnavailableDetails {
    title: string
    message: string
}

@element('flipp-item-dialog')
export class ItemDialog extends Component<{ details: ItemDialogDetails }, any> {
    @inject config: ConfigService
    @handler details: ItemDialogDetails

    refs: {
        content: HTMLElement
        dialogWrapper: HTMLElement
        subtitle: HTMLElement
        dealDetails: HTMLElement
    }

    state = {
        scrollOffset: 170, // Assign default scroll offset
        forceRerender: false,
    }

    onAttach() {
        window.addEventListener(
            'mobile-banner-closed',
            this.onBannerClosed.bind(this)
        )
    }

    onDetach() {
        window.removeEventListener('mobile-banner-closed', this.onBannerClosed)
    }

    onBannerClosed() {
        this.invalidate()
    }

    onRender() {
        window.addEventListener('resize', this._handleResize)
        this.refs.content.addEventListener('scroll', this._handleScroll)

        if (this.state.forceRerender) {
            this.state.forceRerender = false
            setTimeout(() => {
                this.invalidate()
            }, 0)
        }
    }

    invalidate() {
        window.removeEventListener('resize', this._handleResize)
        if (this.refs && this.refs.content) {
            this.refs.content.removeEventListener('scroll', this._handleScroll)
        }
        return super.invalidate()
    }

    onAttributeChange(name: string, oldVal: string, newVal: string) {
        // Block changes to class from triggering new dialog render
        if (name === 'class') return
        super.onAttributeChange(name, oldVal, newVal)
    }

    render() {
        const content = this._getTranscludedContent()
        if (!content) {
            /* HACK ALERT: If we don't have access to any of the transcluded content, render with everything
             * as hidden (so we don't show a gorped layout) and then trigger a re-render from onRender.*/
            this.state.forceRerender = true
        }
        // Find the number of CTA buttons on the Item pop
        const numCta = this._getCtaButtonCount()

        let offsets: { [key: string]: number } = {}
        let itemStyle: any = {}
        let subtitleDivHeight: number

        const type = this.getAttribute('data-type')
        if ((type === 'item' || !type) && !this.hasAttribute('data-indexed')) {
            // Determine the number of lines the title will consume
            const numLines = this._getNumTitleLines()
            const dealsSection = this._findContentSlot('dealDetails')
            const subtitleSection = this._findContentSlot('subtitle')
            if (subtitleSection instanceof HTMLElement) {
                const subtitleDivs = subtitleSection.getElementsByTagName('div')
                subtitleDivHeight = subtitleDivs[0].offsetHeight
            } else if (subtitleSection instanceof virtualDom.VNode) {
                subtitleDivHeight = 18
            }
            /* 23 = lineHeight, 20 = padding-top */
            offsets['reviews'] =
                Math.min(numLines, 2) * 23 +
                (this._findContentSlot('ratings') ? 22 : 0)
            offsets['subtitle'] =
                offsets['reviews'] + (subtitleSection ? 27 : 0)
            offsets['dealDetails'] =
                offsets['subtitle'] + (dealsSection ? 44 : 0)
            offsets['ctaArea'] =
                offsets['dealDetails'] +
                (this._findContentSlot('cta') ? 42 : 0) +
                // Add extra padding if there is no deals section to account for difference in height between
                // deals section and subtitle
                (!dealsSection && subtitleSection ? subtitleDivHeight : 0)

            this.state.scrollOffset = offsets['ctaArea'] + 60 // 60 = height of CTA area + padding
            itemStyle = {
                'padding-top': `${this.state.scrollOffset}px`,
                height:
                    this.state.scrollOffset > 0
                        ? `calc(100% - ${this.state.scrollOffset}px)`
                        : '100%',
            }
        } else {
            offsets = {
                ratings: 0,
                reviews: 0,
                subtitle: 0,
                dealDetails: 0,
                ctaArea: 0,
            }
            this.state.scrollOffset = 0
        }

        return (
            <template>
                <MobileBanner />
                <ItemDialogHeader></ItemDialogHeader>
                <div
                    ref="dialogWrapper"
                    classList={{
                        'single-cta': numCta === 1,
                        unavailable: this.details.unavailable !== null,
                    }}
                >
                    {this.details.unavailable !== null ? (
                        <div
                            class="unavailable-msg"
                            fill-absolute
                            flex-vertical
                        >
                            <div>
                                <h2>{this.details.unavailable.title}</h2>
                                <div>{this.details.unavailable.message}</div>
                            </div>
                        </div>
                    ) : null}

                    <div class="item-info-image" flex-vertical>
                        {this.details && this.details.itemImageUrl ? (
                            <FitImageElement
                                alt=""
                                contain
                                href={this.details.itemImageUrl}
                            />
                        ) : null}
                    </div>
                    {this.details && this.details.merchantLogoUrl ? (
                        <LazyImage
                            class="logo"
                            classList={{ hidden: content == null }}
                            alt=""
                            fit
                            contain
                            dataSrc={this.details.merchantLogoUrl}
                        />
                    ) : null}

                    <h2
                        class="slideable title"
                        classList={{ hidden: content == null }}
                    >
                        <content slot="title" />
                    </h2>
                    <div
                        class="slideable fade ratings"
                        classList={{ hidden: content == null }}
                        ref="ratings"
                        style={{ top: `${offsets['reviews']}px` }}
                    >
                        <content slot="ratings" />
                    </div>
                    <h1
                        class="slideable subtitle"
                        classList={{ hidden: content == null }}
                        ref="subtitle"
                        style={{ top: `${offsets['subtitle']}px` }}
                    >
                        <content slot="subtitle" />
                    </h1>
                    <h1
                        class="slideable deal-details"
                        classList={{ hidden: content == null }}
                        ref="dealDetails"
                        style={{
                            left: '40%',
                            top: `${offsets['dealDetails']}px`,
                        }}
                    >
                        <content slot="dealDetails" />
                    </h1>
                    <div
                        class="slideable cta-area"
                        classList={{ hidden: content == null }}
                        style={{ top: `${offsets['ctaArea']}px` }}
                    >
                        <content slot="cta" />
                    </div>
                    <div class="scrolled-header"></div>
                    <div class="cover"></div>

                    <div
                        class="slideable item-info"
                        classList={{ hidden: content == null }}
                        style={itemStyle}
                        ref="content"
                    >
                        <content />
                    </div>

                    <button
                        class="close-button"
                        classList={{ hidden: content == null }}
                        title={i18n('close_dialog_button')}
                        aria-label={i18n('close_dialog_button')}
                        onClick={this._handleCloseButtonClick}
                    >
                        {icons.roundedX}
                    </button>
                </div>
            </template>
        )
    }

    private _getCtaButtonCount = () => {
        let ctaNum = 1
        const node = this._findContentSlot('cta')
        if (node instanceof HTMLElement) {
            ctaNum = node.querySelectorAll('.flipp-button').length
        } else if (node instanceof virtualDom.VNode) {
            const findItems = function (node: virtualDom.VNode) {
                let buttons = 0
                if (node.children) {
                    node.children.forEach((child) => {
                        buttons += findItems(child as virtualDom.VNode)
                    })
                }

                if (
                    node.properties &&
                    node.properties.attributes &&
                    node.properties.attributes['class'] === 'flipp-button'
                ) {
                    buttons++
                } else if ((node as any)._cls) {
                    // Widget case
                    const widget = node as any
                    const attributes = widget._attributes
                    if (
                        attributes &&
                        attributes.attributes &&
                        attributes.attributes['is'] === 'flipp-clip-ecom-item'
                    ) {
                        buttons++
                    }
                }

                return buttons
            }

            ctaNum = findItems(node)
        }
        return ctaNum
    }

    private _getItemTitle = () => {
        const node = this._findContentSlot('title')
        if (node instanceof HTMLElement) {
            return node.innerHTML
        } else if (node instanceof virtualDom.VNode) {
            if (node.children && node.children.length === 1) {
                return (node.children[0] as any).text
            }
        }
        return ''
    }

    private _getTranscludedContent = () => {
        if (this._transcludedContent.length > 0) {
            return this._transcludedContent
        } else {
            return this._transcludedVTree
        }
    }

    private _findContentSlot = (slotName: string) => {
        const nodes = this._getTranscludedContent()
        if (!nodes) return null
        for (let idx = 0; idx < nodes.length; idx++) {
            if (nodes[idx] instanceof virtualDom.VNode) {
                const node = nodes[idx] as virtualDom.VNode
                if (node.properties.attributes['content-slot'] === slotName) {
                    return node
                }
            } else {
                const node = nodes[idx] as HTMLElement
                if (node.getAttribute('content-slot') === slotName) {
                    return node
                }
            }
        }
        return null
    }

    private _getNumTitleLines = () => {
        let titleWidth = 0
        const title = this._getItemTitle()
        if (!title) return 1

        /* Hack to calclulate the amount of space the title will take. */
        const body = document.getElementsByTagName('body')[0]
        const container = document.createElement('div')
        container.setAttribute('class', 'sizetester')
        container.appendChild(document.createTextNode(title))
        body.appendChild(container)
        titleWidth = container.offsetWidth
        body.removeChild(container)

        /* Calculate the number of lines the title will take up at the current width of the
         * Dialog. The offsetWidth is multiplied by 0.4 because the right side content area of
         * the dialog only consumes 60% of the available width (-45 accounts for padding) */
        return Math.floor(titleWidth / (this.offsetWidth * 0.6 - 45)) + 1
    }

    private _handleResize = () => {
        this.invalidate()
    }

    private _handleScroll = () => {
        // Don't have custom scroll behaviour if on mobile
        const mql = window.matchMedia('(max-width: 600px)')
        if (mql.matches) return

        const styles = window.getComputedStyle(this.refs.content)
        const padding = parseInt(styles.getPropertyValue('padding-top'), 10)
        if (isNaN(padding)) return

        /* 'scrollOffset' defined is the max padding so minus the current padding to account for current animation */
        const scrollOffset = this.state.scrollOffset
        const calc = this.refs.content.scrollTop + scrollOffset - padding
        if (
            this.refs.content.scrollHeight !== this.refs.content.offsetHeight &&
            (calc === scrollOffset || calc + padding === scrollOffset)
        ) {
            this.refs.dealDetails.style.left = '40%'
            this.classList.remove('slide')
        } else if (calc >= 0 && !this.classList.contains('slide')) {
            const elements = this.querySelectorAll('.slideable')
            for (let idx = 0; idx < elements.length; idx++) {
                const element = elements[idx] as HTMLElement
                element.style.transition =
                    'all 0.5s cubic-bezier(0.35, 0, 0.26, 1)'
            }

            this.refs.dealDetails.style.left = `calc(10% + ${this.refs.subtitle.offsetWidth + 10}px)`
            this.classList.add('slide')
        }
    }

    private _handleCloseButtonClick = () => {
        this.dispatchEvent(
            new Event('flipp-dialog-close', {
                bubbles: true,
                cancelable: false,
            })
        )
    }
}

@element('flipp-item-dialog-header')
export class ItemDialogHeader extends Component<any, any> {
    @inject config: ConfigService

    render() {
        return (
            <template>
                <header
                    flex-horizontal
                    style={{ backgroundColor: this.config.getThemeColor() }}
                >
                    <button
                        class="back-button"
                        title={i18n('close_dialog_button')}
                        aria-label={i18n('close_dialog_button')}
                        onClick={this._handleCloseButtonClick}
                    >
                        {icons.backArrow}
                        {i18n('back_button')}
                    </button>
                    {this.config.EXPERIENCE !== Experience.SDE && (
                        <span>{icons.flippLogo}</span>
                    )}
                </header>
            </template>
        )
    }

    private _handleCloseButtonClick = () => {
        this.dispatchEvent(
            new Event('flipp-dialog-close', {
                bubbles: true,
                cancelable: false,
            })
        )
    }
}
